'use client'

/* ─────────────────────────────────────────────────────────────────────────
 * Contact — the brief, filled in on the same desk.
 *
 * Nothing here is invented: the board, the paper, the two faces, the one
 * light up and to the left and the single motion curve are all borrowed from
 * the mat next door and from the About sheets. The only new object is the
 * brief itself — a form printed on a cream sheet, its fields ruled in dashed
 * pencil rather than boxed in chrome, because a box with a border radius is
 * furniture from a different site.
 *
 * What the sheet collects goes straight into the studio's own drawer: the
 * submission is written to the project's `contact_submissions` collection by
 * /api/contact, so enquiries are read in the CMS rather than in an inbox.
 * ───────────────────────────────────────────────────────────────────────── */

import type { CSSProperties, FC, FormEvent, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { ChevronDown } from 'lucide-react'

import { gsap, ScrollTrigger } from '@/lib/animations/gsap-setup'
import { IMG_BASE, MOODS, STUDIO } from '@/components/sections/cutout/data'

import {
    CoffeeRing,
    Paperclip,
    Pushpin,
    Scissors,
    Tape
} from '@/components/sections/cutout/props'

import {
    AFTER,
    BUDGETS,
    CONTACT_FRAME,
    NOTES
} from './data'

/** The grease-pencil ring the section draws round a chosen mood. Same path as
 *  the About sheets, stretched to whatever it is circling. */
const RING_D =
    'M12 26 C12 8 42 4 62 5 C92 6 112 14 110 28 C108 42 78 49 54 47 C28 45 10 38 14 22'

interface RingProps {
    stroke: string
    className?: string
    /** Drawn by the scroll timeline, or by CSS the moment it appears. */
    live?: boolean
}

const PencilRing: FC<RingProps> = ({ stroke, className, live }): ReactNode => (
    <svg
        className={`${live ? 'ct-chipring' : 'ct-ring'} ${className ?? ''}`}
        viewBox='0 0 120 52'
        preserveAspectRatio='none'
        aria-hidden
    >
        <path
            d={RING_D}
            fill='none'
            stroke={stroke}
            strokeWidth='2.6'
            strokeLinecap='round'
        />
    </svg>
)

const PencilArrow: FC = (): ReactNode => (
    <svg className='ct-arrow' viewBox='0 0 90 34' fill='none' aria-hidden>
        <path
            d='M4 9 Q34 30 84 20'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
        />
        <path
            d='M84 20 L70 15 M84 20 L71 27'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
        />
    </svg>
)

interface ShotProps {
    src: string
    alt: string
    className?: string
    eager?: boolean
}

/** A print on the desk, developing behind a paper veil that wipes off it. */
const Shot: FC<ShotProps> = ({ src, alt, className, eager }): ReactNode => (
    <div className={`ct-shot ${className ?? ''}`} data-shot>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
            src={IMG_BASE + src}
            alt={alt}
            loading={eager ? 'eager' : 'lazy'}
            draggable={false}
        />
        <span className='ct-veil' aria-hidden />
    </div>
)

interface Brief {
    name: string
    email: string
    company: string
    look: string
    dates: string
    budget: string
    message: string
    /** The honeypot. People never see it, bots fill it in. */
    website: string
}

const EMPTY: Brief = {
    name: '',
    email: '',
    company: '',
    look: '',
    dates: '',
    budget: '',
    message: '',
    website: ''
}

type Status = 'idle' | 'sending' | 'sent' | 'error'

export const ContactDesk: FC = (): ReactNode => {
    const rootRef = useRef<HTMLDivElement>(null)

    const [brief, setBrief] = useState<Brief>(EMPTY)
    const [status, setStatus] = useState<Status>('idle')
    const [error, setError] = useState<string>('')
    const [badField, setBadField] = useState<string>('')
    const [ref, setRef] = useState<string>('')

    const set = (key: keyof Brief) => (
        event: { target: { value: string } }
    ): void => {
        setBrief((prev) => ({ ...prev, [key]: event.target.value }))
    }

    const submit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault()

        if (status === 'sending') return

        setStatus('sending')
        setError('')
        setBadField('')

        try {
            const res = await fetch('/api/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(brief)
            })

            const data = (await res.json()) as {
                ok: boolean
                id?: number
                error?: string
                field?: string
            }

            if (!res.ok || !data.ok) {
                setStatus('error')
                setError(data.error ?? 'The brief did not save. Try again in a moment.')
                setBadField(data.field ?? '')

                return
            }

            setRef(String(data.id ?? 0).padStart(4, '0'))
            setBrief(EMPTY)
            setStatus('sent')
        } catch {
            setStatus('error')
            setError('The brief did not save. Check your connection and try again.')
        }
    }

    useEffect(() => {
        const root = rootRef.current
        if (!root) return

        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches

        const ctx = gsap.context(() => {
            /* Hover is handed back only once a block has landed — a CSS
             * transition on `transform` while GSAP writes `transform` smears
             * the reveal, so the transition arrives with `.is-in`. */
            const settle = (items: ArrayLike<Element>) => {
                Array.from(items).forEach((el) => el.classList.add('is-in'))
            }

            /* Reduced motion gets the finished page and no triggers at all. */
            if (reduce) {
                const all = root.querySelectorAll('[data-reveal], [data-hero]')
                gsap.set(all, { opacity: 1, y: 0 })
                gsap.set('.ct-veil', { scaleX: 0 })
                gsap.set('.ct-shot img', { scale: 1 })
                gsap.set('.ct-ring path', { strokeDashoffset: 0 })
                settle(root.querySelectorAll('[data-reveal]'))

                return
            }

            /* ── the hero lands ───────────────────────────────────────────
             * Pencil, then the cut blocks, then the instruction. Every tween
             * is a `fromTo` so each block keeps the rotation the stylesheet
             * gave it instead of being flattened square on the last frame. */
            const tl = gsap.timeline({ defaults: { ease: 'power3.out' } })

            tl.fromTo(
                '[data-hero="label"]',
                { opacity: 0, y: 16 },
                { opacity: 1, y: 0, duration: 0.7 },
                0.1
            )
                .fromTo(
                    '[data-hero="block"]',
                    { opacity: 0, y: 26, scale: 0.94, filter: 'blur(6px)' },
                    {
                        opacity: 1,
                        y: 0,
                        scale: 1,
                        filter: 'blur(0px)',
                        duration: 0.9,
                        stagger: 0.13
                    },
                    0.24
                )
                .fromTo(
                    '[data-hero="note"]',
                    { opacity: 0, y: 16 },
                    { opacity: 1, y: 0, duration: 0.7 },
                    0.72
                )
                /* y and opacity only — each slip carries its OWN rotation in
                 * the stylesheet, and tweening `rotate` here would land all
                 * four on the same angle. */
                .fromTo(
                    '[data-hero="slip"]',
                    { opacity: 0, y: 24 },
                    {
                        opacity: 1,
                        y: 0,
                        duration: 0.9,
                        stagger: 0.1,
                        onComplete: () =>
                            settle(root.querySelectorAll('[data-hero="slip"]'))
                    },
                    0.5
                )

            /* ── scroll reveals ───────────────────────────────────────── */
            const groups = gsap.utils.toArray<HTMLElement>('[data-reveal-group]')

            groups.forEach((group) => {
                const items = group.querySelectorAll('[data-reveal]')
                if (!items.length) return

                gsap.fromTo(
                    items,
                    { opacity: 0, y: 28 },
                    {
                        opacity: 1,
                        y: 0,
                        duration: 0.85,
                        ease: 'power3.out',
                        stagger: 0.08,
                        onComplete: () => settle(items),
                        scrollTrigger: {
                            trigger: group,
                            start: 'top 82%',
                            once: true
                        }
                    }
                )
            })

            /* ── the prints develop where they sit ────────────────────── */
            gsap.utils.toArray<HTMLElement>('[data-shot]').forEach((shot) => {
                gsap.timeline({
                    scrollTrigger: { trigger: shot, start: 'top 88%', once: true }
                })
                    .to(shot.querySelector('.ct-veil'), {
                        scaleX: 0,
                        duration: 0.9,
                        ease: 'power3.inOut'
                    })
                    .to(
                        shot.querySelector('img'),
                        { scale: 1, duration: 1.25, ease: 'power3.out' },
                        0
                    )
            })

            /* ── the grease pencil draws itself on ────────────────────── */
            gsap.utils.toArray<SVGPathElement>('.ct-ring path').forEach((path) => {
                const anchor = path.closest('figure, li') ?? path

                gsap.to(path, {
                    strokeDashoffset: 0,
                    duration: 0.6,
                    ease: 'power2.inOut',
                    scrollTrigger: { trigger: anchor, start: 'top 88%', once: true }
                })
            })
        }, root)

        /* The display face is wider than the fallback and lands after first
         * paint, so anything measured before that is measured too short. */
        document.fonts?.ready.then(() => ScrollTrigger.refresh()).catch(() => {})

        return () => ctx.revert()
    }, [])

    /* The sheet is swapped for the receipt once it is filed, so the triggers
     * that were measured against the form need remeasuring underneath. */
    useEffect(() => {
        if (status === 'sent') ScrollTrigger.refresh()
    }, [status])

    return (
        <div className='ct-root' ref={rootRef}>
            <div className='ct-stains' aria-hidden>
                <div className='ct-stain ct-stain--a'>
                    <CoffeeRing uid='ct-ring-a' />
                </div>
                <div className='ct-stain ct-stain--b'>
                    <CoffeeRing uid='ct-ring-b' />
                </div>
            </div>

            {/* ── the sheet head ──────────────────────────────────────── */}
            <header className='ct-hero'>
                <div className='ct-hero-type'>
                    <p className='ct-pencil' data-hero='label'>
                        CONTACT — SHEET 07 / {STUDIO.name}
                    </p>

                    <h1 className='ct-h1'>
                        <span className='ct-cut ct-cut--pencil' data-hero='block'>
                            SO WHAT ARE
                        </span>
                        <span className='ct-cut ct-cut--dark' data-hero='block'>
                            WE SHOOTING,
                        </span>
                        <span className='ct-cut ct-cut--yellow' data-hero='block'>
                            and what for?
                        </span>
                    </h1>

                    <p className='ct-note' data-hero='note'>
                        <span>FILL THE BRIEF IN — IT TAKES A MINUTE</span>
                        <PencilArrow />
                    </p>

                    <div className='ct-scissors' aria-hidden>
                        <Scissors uid='ct-scissors' />
                    </div>
                </div>

                <ul className='ct-heronotes'>
                    {NOTES.map((n, i) => (
                        <li
                            className='ct-noteslip'
                            key={n.k}
                            data-hero='slip'
                            style={{ '--rot': `${(i % 3) - 1}deg` } as CSSProperties}
                        >
                            <span className='ct-pin' aria-hidden>
                                <Pushpin
                                    uid={`ct-pin-${i}`}
                                    hue={i % 2 ? '#CC3A22' : '#E4C33C'}
                                />
                            </span>
                            <strong>{n.k}</strong>
                            <span>{n.v}</span>
                        </li>
                    ))}
                </ul>
            </header>

            {/* ── the brief ───────────────────────────────────────────── */}
            <section className='ct-brief' data-reveal-group aria-labelledby='brief-head'>
                <article className='ct-sheet' data-reveal>
                    <div className='ct-clip' aria-hidden>
                        <Paperclip uid='ct-clip' />
                    </div>

                    <p className='ct-label'>THE BRIEF</p>

                    <h2 className='ct-h2' id='brief-head'>
                        {status === 'sent'
                            ? 'Filed, and on my desk.'
                            : 'Tell me what the photographs are for.'}
                    </h2>

                    {status === 'sent' ? (
                        <div className='ct-done'>
                            <p className='ct-stamp' aria-hidden>
                                RECEIVED
                            </p>

                            <p className='ct-body'>
                                Your brief is in the drawer with the reference below. I read
                                everything myself and answer within two working days — if the
                                job is not for me I will say so, and tell you who it is for.
                            </p>

                            <dl className='ct-receipt'>
                                <div>
                                    <dt>Reference</dt>
                                    <dd>BRIEF-{ref}</dd>
                                </div>
                                <div>
                                    <dt>Filed</dt>
                                    <dd>
                                        {new Date().toLocaleDateString('en-GB', {
                                            day: '2-digit',
                                            month: 'short',
                                            year: 'numeric'
                                        })}
                                    </dd>
                                </div>
                                <div>
                                    <dt>Reply by</dt>
                                    <dd>Two working days</dd>
                                </div>
                            </dl>

                            <div className='ct-ctarow'>
                                <button
                                    type='button'
                                    className='ct-btn'
                                    onClick={() => {
                                        setStatus('idle')
                                        setRef('')
                                    }}
                                >
                                    Send another
                                </button>

                                <Link href='/' className='ct-mail'>
                                    Back to the mat
                                </Link>
                            </div>
                        </div>
                    ) : (
                        <form className='ct-form' onSubmit={submit} noValidate>
                            <div className='ct-row'>
                                <div className='ct-field' data-reveal>
                                    <label htmlFor='ct-name'>Your name</label>
                                    <input
                                        id='ct-name'
                                        name='name'
                                        className='ct-input'
                                        type='text'
                                        autoComplete='name'
                                        required
                                        maxLength={120}
                                        aria-invalid={badField === 'name'}
                                        value={brief.name}
                                        onChange={set('name')}
                                        placeholder='Maren Okonkwo'
                                    />
                                </div>

                                <div className='ct-field' data-reveal>
                                    <label htmlFor='ct-email'>Email</label>
                                    <input
                                        id='ct-email'
                                        name='email'
                                        className='ct-input'
                                        type='email'
                                        autoComplete='email'
                                        required
                                        maxLength={180}
                                        aria-invalid={badField === 'email'}
                                        value={brief.email}
                                        onChange={set('email')}
                                        placeholder='you@studio.com'
                                    />
                                </div>
                            </div>

                            <div className='ct-row'>
                                <div className='ct-field' data-reveal>
                                    <label htmlFor='ct-company'>Company or publication</label>
                                    <input
                                        id='ct-company'
                                        name='company'
                                        className='ct-input'
                                        type='text'
                                        autoComplete='organization'
                                        maxLength={160}
                                        value={brief.company}
                                        onChange={set('company')}
                                        placeholder='Optional'
                                    />
                                </div>

                                <div className='ct-field' data-reveal>
                                    <label htmlFor='ct-dates'>Shoot dates</label>
                                    <input
                                        id='ct-dates'
                                        name='dates'
                                        className='ct-input'
                                        type='text'
                                        maxLength={160}
                                        value={brief.dates}
                                        onChange={set('dates')}
                                        placeholder='Early March, two days'
                                    />
                                </div>
                            </div>

                            <fieldset className='ct-looks' data-reveal>
                                <legend>Which look, if you already know</legend>

                                <div className='ct-chips'>
                                    {MOODS.map((m) => {
                                        const on = brief.look === m.id

                                        return (
                                            <label
                                                className={`ct-chip ${on ? 'is-on' : ''}`}
                                                key={m.id}
                                                style={
                                                    {
                                                        '--paper': m.paper,
                                                        '--ink': m.ink
                                                    } as CSSProperties
                                                }
                                            >
                                                <input
                                                    type='radio'
                                                    name='look'
                                                    value={m.id}
                                                    checked={on}
                                                    onChange={set('look')}
                                                />
                                                <span className='ct-chip-word'>{m.word}</span>
                                                <span className='ct-chip-stock'>{m.stock}</span>

                                                {on && <PencilRing stroke='#FF5B39' live />}
                                            </label>
                                        )
                                    })}
                                </div>
                            </fieldset>

                            <div className='ct-field ct-field--select' data-reveal>
                                <label htmlFor='ct-budget'>Budget band</label>

                                <div className='ct-selectwrap'>
                                    <select
                                        id='ct-budget'
                                        name='budget'
                                        className='ct-select'
                                        value={brief.budget}
                                        onChange={set('budget')}
                                    >
                                        <option value=''>Say later</option>

                                        {BUDGETS.map((b) => (
                                            <option value={b} key={b}>
                                                {b}
                                            </option>
                                        ))}
                                    </select>

                                    <ChevronDown className='ct-caret' aria-hidden />
                                </div>
                            </div>

                            <div className='ct-field' data-reveal>
                                <label htmlFor='ct-message'>What are the photographs for</label>
                                <textarea
                                    id='ct-message'
                                    name='message'
                                    className='ct-input ct-textarea'
                                    rows={5}
                                    required
                                    maxLength={4000}
                                    aria-invalid={badField === 'message'}
                                    aria-describedby='ct-message-hint'
                                    value={brief.message}
                                    onChange={set('message')}
                                    placeholder='The room, the deadline, and what happens if the pictures are wrong.'
                                />
                                <p className='ct-hint' id='ct-message-hint'>
                                    No mood board needed. Plain words are faster.
                                </p>
                            </div>

                            {/* the honeypot — off-screen, never tabbed to */}
                            <div className='ct-hp' aria-hidden>
                                <label htmlFor='ct-website'>Website</label>
                                <input
                                    id='ct-website'
                                    name='website'
                                    type='text'
                                    tabIndex={-1}
                                    autoComplete='off'
                                    value={brief.website}
                                    onChange={set('website')}
                                />
                            </div>

                            <div className='ct-submit' data-reveal>
                                <button
                                    type='submit'
                                    className='ct-btn'
                                    disabled={status === 'sending'}
                                >
                                    {status === 'sending' ? 'Filing…' : 'Send the brief'}
                                </button>

                                <a href={`mailto:${STUDIO.mail}`} className='ct-mail'>
                                    or just email {STUDIO.mail}
                                </a>
                            </div>

                            <p
                                className={`ct-status ${status === 'error' ? 'is-bad' : ''}`}
                                role='status'
                                aria-live='polite'
                            >
                                {status === 'error' ? error : ''}
                                {status === 'sending' ? 'Filing the brief…' : ''}
                            </p>
                        </form>
                    )}
                </article>

                <figure className='ct-side' data-reveal>
                    <div className='ct-tape ct-tape--tl' aria-hidden>
                        <Tape uid='ct-tape-1' />
                    </div>

                    <Shot
                        src={CONTACT_FRAME.src}
                        alt='A brief and a loupe on a table in Rome, lit by one practical lamp'
                        eager
                    />

                    <figcaption>{CONTACT_FRAME.caption}</figcaption>
                </figure>
            </section>

            {/* ── what happens next ───────────────────────────────────── */}
            <section className='ct-after' data-reveal-group aria-labelledby='after-head'>
                <div className='ct-sec-head'>
                    <p className='ct-pencil' data-reveal>
                        ONCE THE SHEET LEAVES YOUR HANDS
                    </p>
                    <h2 className='ct-h2 ct-h2--onmat' id='after-head' data-reveal>
                        Three things happen, in this order.
                    </h2>
                </div>

                <ol className='ct-cards'>
                    {AFTER.map((s, i) => (
                        <li
                            className='ct-card'
                            key={s.n}
                            data-reveal
                            style={{ '--rot': `${i % 2 ? 1.1 : -1.3}deg` } as CSSProperties}
                        >
                            <span className='ct-card-n'>
                                {s.n}
                                <PencilRing stroke='#B23A2B' className='ct-ring--num' />
                            </span>
                            <h3>{s.name}</h3>
                            <p>{s.body}</p>
                        </li>
                    ))}
                </ol>
            </section>

            <footer className='ct-foot'>
                <p>
                    {STUDIO.name} · {STUDIO.role} · {STUDIO.city}
                </p>
                <Link href='/about'>the studio, in full →</Link>
            </footer>

            <style>{`
                /* ── the board ───────────────────────────────────────────
                 * The same drawn mat as the About sheets: the section's green,
                 * a rule every centimetre with a heavy one every fifth, and a
                 * mottle over it, because a self-healing mat is a pressed
                 * sheet and never one flat colour. */
                .ct-root {
                    --minor: 34px;
                    --major: 170px;
                    position: relative;
                    min-height: 100vh;
                    overflow: hidden;
                    color: #F4F9EC;
                    background-color: #2C4E3E;
                    background-image:
                        repeating-linear-gradient(to right, rgba(238,244,222,.30) 0 1px, transparent 1px var(--major)),
                        repeating-linear-gradient(to bottom, rgba(238,244,222,.30) 0 1px, transparent 1px var(--major)),
                        repeating-linear-gradient(to right, rgba(233,240,216,.14) 0 1px, transparent 1px var(--minor)),
                        repeating-linear-gradient(to bottom, rgba(233,240,216,.14) 0 1px, transparent 1px var(--minor)),
                        radial-gradient(60% 40% at 18% 12%, rgba(53,96,73,.85), transparent 70%),
                        radial-gradient(50% 35% at 82% 46%, rgba(53,96,73,.7), transparent 72%),
                        radial-gradient(45% 30% at 30% 84%, rgba(41,74,58,.9), transparent 70%);
                    font-family: var(--font-geist-sans), system-ui, sans-serif;
                }
                .ct-root::before {
                    content: '';
                    position: absolute;
                    inset: 0;
                    pointer-events: none;
                    background-image:
                        linear-gradient(74deg, transparent 49.7%, rgba(232,240,222,.10) 49.7% 49.9%, transparent 49.9%),
                        linear-gradient(-58deg, transparent 31.4%, rgba(232,240,222,.08) 31.4% 31.55%, transparent 31.55%),
                        linear-gradient(21deg, transparent 68%, rgba(232,240,222,.07) 68% 68.15%, transparent 68.15%);
                    background-size: 100% 900px, 100% 1400px, 100% 1100px;
                }
                .ct-stains { position: absolute; inset: 0; pointer-events: none; }
                .ct-stain { position: absolute; opacity: .5; }
                .ct-stain--a { width: 210px; height: 210px; top: 58vh; right: 7vw; }
                .ct-stain--b { width: 150px; height: 150px; top: 168vh; left: 4vw; }

                /* ── shared type: two faces and no third ─────────────── */
                .ct-pencil, .ct-label, .ct-note, .ct-side figcaption,
                .ct-noteslip strong, .ct-foot, .ct-btn, .ct-mail,
                .ct-field label, .ct-looks legend, .ct-hint, .ct-status,
                .ct-chip-stock, .ct-receipt dt, .ct-receipt dd {
                    font-family: var(--font-geist-mono), ui-monospace, monospace;
                }
                .ct-h1, .ct-h2, .ct-cut, .ct-card h3, .ct-chip-word, .ct-stamp {
                    font-family: var(--font-archivo-black), Impact, sans-serif;
                    font-weight: 400;
                }
                .ct-pencil {
                    margin: 0;
                    font-size: clamp(10px, 1.5vw, 12px);
                    letter-spacing: .16em;
                    text-transform: uppercase;
                    color: rgba(244,249,236,.9);
                }
                .ct-label {
                    margin: 0 0 10px;
                    font-size: 10px;
                    letter-spacing: .16em;
                    text-transform: uppercase;
                    opacity: .5;
                }

                /* THE REVEAL CONTRACT — opacity only. Every rule below owns a
                 * rotation, and a transform written here is the one GSAP would
                 * inherit and then flatten. */
                [data-reveal], [data-hero] { opacity: 0; }

                .ct-veil {
                    position: absolute;
                    inset: 0;
                    background: #EFE7D6;
                    transform: scaleX(1);
                    transform-origin: 100% 50%;
                    pointer-events: none;
                }

                .ct-shot {
                    position: relative;
                    overflow: hidden;
                    background: #1B1712;
                    border-radius: 1px;
                }
                .ct-shot img {
                    display: block;
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                    transform: scale(1.09);
                    user-select: none;
                    -webkit-user-drag: none;
                }

                /* ── hero ────────────────────────────────────────────── */
                .ct-hero {
                    position: relative;
                    display: grid;
                    grid-template-columns: minmax(0, 1.15fr) minmax(0, .85fr);
                    align-items: center;
                    gap: clamp(28px, 5vw, 72px);
                    max-width: 1240px;
                    margin: 0 auto;
                    padding: clamp(96px, 16vh, 190px) clamp(22px, 5vw, 56px) clamp(50px, 8vh, 100px);
                }
                .ct-hero-type { position: relative; }
                .ct-h1 {
                    display: flex;
                    flex-direction: column;
                    align-items: flex-start;
                    gap: 10px;
                    margin: 18px 0 0;
                    line-height: .96;
                }
                .ct-cut {
                    display: inline-block;
                    font-size: clamp(32px, 6.8vw, 72px);
                    letter-spacing: -.02em;
                    transform-origin: 50% 50%;
                }
                .ct-cut--pencil {
                    font-family: var(--font-geist-mono), ui-monospace, monospace;
                    font-size: clamp(13px, 2vw, 18px);
                    letter-spacing: .2em;
                    color: rgba(244,249,236,.82);
                    transform: rotate(-1.8deg);
                    margin-bottom: 2px;
                }
                .ct-cut--dark {
                    background: #241C15;
                    color: #F5EFE2;
                    padding: .12em .26em .2em;
                    transform: rotate(-1.6deg);
                    box-shadow: 3px 7px 0 rgba(20,16,10,.34);
                }
                .ct-cut--yellow {
                    background: #EFB61F;
                    color: #241C10;
                    padding: .1em .26em .18em;
                    margin-left: clamp(18px, 6vw, 92px);
                    transform: rotate(4.4deg);
                    box-shadow: 3px 7px 0 rgba(20,16,10,.3);
                }
                .ct-note {
                    display: inline-flex;
                    align-items: center;
                    gap: 10px;
                    margin: clamp(28px, 5vh, 46px) 0 0;
                    font-size: clamp(10px, 1.5vw, 12px);
                    letter-spacing: .16em;
                    text-transform: uppercase;
                    color: rgba(244,249,236,.78);
                    transform: rotate(1.2deg);
                }
                .ct-arrow { width: 74px; height: 28px; flex: 0 0 auto; }
                .ct-scissors {
                    position: absolute;
                    right: -4%;
                    bottom: -16%;
                    width: 62px;
                    aspect-ratio: 120 / 200;
                    opacity: .9;
                    transform: rotate(-24deg);
                    filter: drop-shadow(2px 6px 6px rgba(12,20,14,.5));
                    pointer-events: none;
                }

                /* ── pinned slips ────────────────────────────────────── */
                .ct-heronotes {
                    display: grid;
                    grid-template-columns: repeat(2, minmax(0, 1fr));
                    gap: clamp(18px, 2.4vw, 28px);
                    margin: 0;
                    padding: 0;
                    list-style: none;
                }
                .ct-noteslip {
                    position: relative;
                    background: #EFE7D6;
                    color: #241F19;
                    padding: 28px 18px 20px;
                    transform: rotate(var(--rot, 0deg));
                    box-shadow: 3px 9px 0 rgba(14,24,18,.28);
                }
                .ct-noteslip.is-in { transition: transform .3s cubic-bezier(.16,1,.3,1); }
                .ct-noteslip.is-in:hover { transform: rotate(var(--rot, 0deg)) translateY(-4px); }
                .ct-pin {
                    position: absolute;
                    top: -11px;
                    left: 50%;
                    width: 24px;
                    height: 24px;
                    transform: translateX(-50%);
                    filter: drop-shadow(1px 3px 3px rgba(20,16,10,.4));
                }
                .ct-noteslip strong {
                    display: block;
                    font-size: 9.5px;
                    font-weight: 500;
                    letter-spacing: .16em;
                    text-transform: uppercase;
                    color: rgba(36,31,25,.5);
                    margin-bottom: 6px;
                }
                .ct-noteslip span { font-size: 13.5px; line-height: 1.5; }

                /* ── the brief ───────────────────────────────────────── */
                .ct-brief {
                    display: grid;
                    grid-template-columns: minmax(0, 1.4fr) minmax(0, .6fr);
                    align-items: start;
                    gap: clamp(26px, 4.5vw, 64px);
                    max-width: 1240px;
                    margin: 0 auto;
                    padding: clamp(40px, 8vh, 90px) clamp(22px, 5vw, 56px);
                }
                .ct-sheet {
                    position: relative;
                    background: #F6F1E6;
                    color: #241F19;
                    padding: clamp(26px, 4vw, 52px);
                    border-radius: 2px;
                    transform: rotate(-.55deg);
                    box-shadow: 6px 16px 0 rgba(14,24,18,.3), 0 2px 0 rgba(255,255,255,.5) inset;
                }
                .ct-clip {
                    position: absolute;
                    top: -26px;
                    right: clamp(28px, 8%, 70px);
                    width: 30px;
                    aspect-ratio: 46 / 110;
                    filter: drop-shadow(1px 3px 3px rgba(20,16,10,.35));
                }
                .ct-h2 {
                    margin: 0 0 18px;
                    font-size: clamp(23px, 3.5vw, 38px);
                    line-height: 1.05;
                    letter-spacing: -.015em;
                }
                .ct-h2--onmat { color: #F7FBF0; margin-top: 8px; }
                .ct-body {
                    margin: 0 0 15px;
                    font-size: clamp(14.5px, 1.35vw, 16.5px);
                    line-height: 1.66;
                    color: rgba(36,31,25,.84);
                    max-width: 62ch;
                }

                /* ── the form, ruled rather than boxed ───────────────── */
                .ct-form { margin-top: 26px; }
                .ct-row {
                    display: grid;
                    grid-template-columns: repeat(2, minmax(0, 1fr));
                    gap: clamp(16px, 2.4vw, 28px);
                }
                .ct-field { margin-bottom: 22px; }
                .ct-field label, .ct-looks legend {
                    display: block;
                    margin-bottom: 5px;
                    padding: 0;
                    font-size: 9.5px;
                    letter-spacing: .16em;
                    text-transform: uppercase;
                    color: rgba(36,31,25,.52);
                }
                .ct-input {
                    display: block;
                    width: 100%;
                    padding: 9px 2px 8px;
                    border: 0;
                    border-bottom: 1px dashed rgba(36,31,25,.34);
                    border-radius: 0;
                    background: transparent;
                    color: #241F19;
                    font-family: var(--font-geist-sans), system-ui, sans-serif;
                    font-size: 15.5px;
                    line-height: 1.5;
                    transition: border-color .2s ease, background .2s ease;
                }
                .ct-input::placeholder { color: rgba(36,31,25,.32); }
                .ct-input:hover { border-bottom-color: rgba(36,31,25,.55); }
                .ct-input:focus {
                    outline: none;
                    border-bottom-color: #B23A2B;
                    background: rgba(178,58,43,.05);
                }
                .ct-input[aria-invalid='true'] { border-bottom-color: #B23A2B; border-bottom-style: solid; }
                .ct-textarea { resize: vertical; min-height: 128px; }
                .ct-hint {
                    margin: 7px 0 0;
                    font-size: 9.5px;
                    letter-spacing: .12em;
                    text-transform: uppercase;
                    color: rgba(36,31,25,.42);
                }

                .ct-selectwrap { position: relative; }
                .ct-select {
                    display: block;
                    width: 100%;
                    padding: 9px 30px 8px 2px;
                    border: 0;
                    border-bottom: 1px dashed rgba(36,31,25,.34);
                    border-radius: 0;
                    background: transparent;
                    color: #241F19;
                    font-family: var(--font-geist-sans), system-ui, sans-serif;
                    font-size: 15.5px;
                    appearance: none;
                    -webkit-appearance: none;
                    cursor: pointer;
                    transition: border-color .2s ease;
                }
                .ct-select:hover { border-bottom-color: rgba(36,31,25,.55); }
                .ct-select:focus { outline: none; border-bottom-color: #B23A2B; }
                .ct-caret {
                    position: absolute;
                    right: 4px;
                    top: 50%;
                    width: 16px;
                    height: 16px;
                    transform: translateY(-60%);
                    color: rgba(36,31,25,.45);
                    pointer-events: none;
                }

                /* ── the five looks, as chips off the drawer ─────────── */
                .ct-looks { margin: 4px 0 26px; padding: 0; border: 0; }
                .ct-chips { display: flex; flex-wrap: wrap; gap: 12px 14px; margin-top: 10px; }
                .ct-chip {
                    position: relative;
                    display: inline-flex;
                    flex-direction: column;
                    gap: 3px;
                    padding: 9px 14px 8px;
                    border-radius: 2px;
                    background: var(--paper);
                    color: var(--ink);
                    cursor: pointer;
                    box-shadow: 2px 4px 0 rgba(10,18,13,.3);
                    transform: rotate(-1.4deg);
                    transition: transform .22s cubic-bezier(.16,1,.3,1),
                                box-shadow .22s cubic-bezier(.16,1,.3,1);
                }
                .ct-chip input {
                    position: absolute;
                    width: 1px;
                    height: 1px;
                    opacity: 0;
                    pointer-events: none;
                }
                .ct-chip:hover { transform: rotate(-1.4deg) translateY(-3px); }
                .ct-chip.is-on {
                    transform: rotate(-1.4deg) translateY(-3px);
                    box-shadow: 3px 8px 0 rgba(10,18,13,.36);
                }
                .ct-chip:focus-within { outline: 2px dashed #B23A2B; outline-offset: 4px; }
                .ct-chip-word { font-size: 13px; letter-spacing: .02em; }
                .ct-chip-stock { font-size: 8.5px; letter-spacing: .12em; text-transform: uppercase; opacity: .72; }

                .ct-hp {
                    position: absolute;
                    left: -9999px;
                    width: 1px;
                    height: 1px;
                    overflow: hidden;
                }

                .ct-submit {
                    display: flex;
                    flex-wrap: wrap;
                    align-items: center;
                    gap: 14px 24px;
                    margin-top: 30px;
                }
                .ct-btn {
                    display: inline-block;
                    padding: 13px 22px 12px;
                    border: 0;
                    border-radius: 2px;
                    background: #241F19;
                    color: #F6F1E6;
                    font-family: var(--font-geist-mono), ui-monospace, monospace;
                    font-size: 12px;
                    letter-spacing: .08em;
                    text-transform: uppercase;
                    text-decoration: none;
                    cursor: pointer;
                    box-shadow: 2px 5px 0 rgba(20,16,10,.3);
                    transition: transform .16s ease, box-shadow .16s ease, opacity .16s ease;
                }
                .ct-btn:hover { transform: translateY(-2px); box-shadow: 3px 8px 0 rgba(20,16,10,.34); }
                .ct-btn:active { transform: none; box-shadow: 1px 2px 0 rgba(20,16,10,.34); }
                .ct-btn:disabled { opacity: .55; cursor: progress; transform: none; box-shadow: 1px 3px 0 rgba(20,16,10,.3); }
                .ct-mail {
                    font-size: 11.5px;
                    letter-spacing: .06em;
                    color: rgba(36,31,25,.72);
                    text-decoration: none;
                    border-bottom: 1px solid rgba(36,31,25,.3);
                    padding-bottom: 2px;
                    transition: color .18s ease, border-color .18s ease;
                }
                .ct-mail:hover { color: #241F19; border-color: #241F19; }
                .ct-status {
                    min-height: 16px;
                    margin: 16px 0 0;
                    font-size: 10.5px;
                    letter-spacing: .12em;
                    text-transform: uppercase;
                    color: rgba(36,31,25,.5);
                }
                .ct-status.is-bad { color: #B23A2B; }

                /* ── the receipt ─────────────────────────────────────── */
                .ct-done { animation: ctFile .7s cubic-bezier(.16,1,.3,1) both; }
                .ct-stamp {
                    display: inline-block;
                    margin: 0 0 20px;
                    padding: 7px 16px 5px;
                    border: 2.5px solid rgba(178,58,43,.65);
                    border-radius: 3px;
                    color: rgba(178,58,43,.78);
                    font-size: clamp(20px, 3.4vw, 30px);
                    letter-spacing: .06em;
                    transform: rotate(-4.6deg);
                    animation: ctStamp .5s .12s cubic-bezier(.16,1,.3,1) both;
                }
                .ct-receipt {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
                    gap: 16px 22px;
                    margin: 26px 0 30px;
                    padding: 20px 0 0;
                    border-top: 1px dashed rgba(36,31,25,.3);
                }
                .ct-receipt div { margin: 0; }
                .ct-receipt dt {
                    margin: 0 0 5px;
                    font-size: 9.5px;
                    letter-spacing: .16em;
                    text-transform: uppercase;
                    color: rgba(36,31,25,.5);
                }
                .ct-receipt dd {
                    margin: 0;
                    font-size: 13.5px;
                    letter-spacing: .06em;
                    color: #241F19;
                }
                .ct-ctarow { display: flex; flex-wrap: wrap; align-items: center; gap: 14px 24px; }
                @keyframes ctFile {
                    from { opacity: 0; transform: translateY(18px); }
                }
                @keyframes ctStamp {
                    from { opacity: 0; transform: rotate(-14deg) scale(1.35); }
                }

                /* ── the taped frame ─────────────────────────────────── */
                .ct-side {
                    position: relative;
                    margin: 0;
                    padding: 11px 11px 0;
                    background: #F6F1E6;
                    box-shadow: 4px 12px 0 rgba(14,24,18,.3);
                    transform: rotate(1.4deg);
                }
                .ct-side .ct-shot { aspect-ratio: 3 / 4; }
                .ct-side figcaption {
                    padding: 10px 2px 12px;
                    font-size: 9.5px;
                    letter-spacing: .14em;
                    text-transform: uppercase;
                    color: rgba(36,31,25,.55);
                }
                .ct-tape { position: absolute; width: 128px; height: 34px; z-index: 3; pointer-events: none;
                    filter: drop-shadow(1px 2px 2px rgba(20,16,10,.22)); }
                .ct-tape--tl { top: -16px; left: -26px; transform: rotate(-28deg); }

                /* ── what happens next ───────────────────────────────── */
                .ct-sec-head { max-width: 1240px; margin: 0 auto clamp(22px, 4vh, 40px); }
                .ct-after {
                    max-width: 1240px;
                    margin: 0 auto;
                    padding: clamp(46px, 9vh, 108px) clamp(22px, 5vw, 56px);
                }
                .ct-sec-head .ct-h2 { max-width: 22ch; }
                .ct-cards {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(238px, 1fr));
                    gap: clamp(18px, 2.4vw, 30px);
                    margin: 0;
                    padding: 0;
                    list-style: none;
                }
                .ct-card {
                    position: relative;
                    background: #F6F1E6;
                    color: #241F19;
                    padding: 26px 22px 24px;
                    border-radius: 2px;
                    transform: rotate(var(--rot, 0deg));
                    box-shadow: 4px 11px 0 rgba(14,24,18,.3);
                }
                .ct-card.is-in {
                    transition: transform .3s cubic-bezier(.16,1,.3,1),
                                box-shadow .3s cubic-bezier(.16,1,.3,1);
                }
                .ct-card.is-in:hover {
                    transform: rotate(var(--rot, 0deg)) translateY(-5px);
                    box-shadow: 6px 16px 0 rgba(14,24,18,.34);
                }
                .ct-card-n {
                    position: relative;
                    display: inline-block;
                    margin-bottom: 16px;
                    padding: 2px 7px;
                    font-family: var(--font-geist-mono), ui-monospace, monospace;
                    font-size: 12px;
                    letter-spacing: .1em;
                    color: #B23A2B;
                }
                .ct-card h3 { margin: 0 0 9px; font-size: 19px; letter-spacing: -.005em; }
                .ct-card p { margin: 0; font-size: 14px; line-height: 1.62; color: rgba(36,31,25,.78); }

                /* ── grease pencil ───────────────────────────────────── */
                .ct-ring, .ct-chipring {
                    position: absolute;
                    left: -8px;
                    top: -8px;
                    width: calc(100% + 16px);
                    height: calc(100% + 16px);
                    overflow: visible;
                    pointer-events: none;
                    z-index: 4;
                }
                .ct-ring path { stroke-dasharray: 340; stroke-dashoffset: 340; }
                .ct-ring--num { left: -6px; top: -5px; width: calc(100% + 12px); height: calc(100% + 10px); }
                /* the chip's ring is not on the scroll clock — it is drawn the
                 * moment the look is chosen */
                .ct-chipring path {
                    stroke-dasharray: 340;
                    stroke-dashoffset: 340;
                    animation: ctDraw .55s cubic-bezier(.16,1,.3,1) forwards;
                }
                @keyframes ctDraw { to { stroke-dashoffset: 0; } }

                .ct-foot {
                    display: flex;
                    flex-wrap: wrap;
                    align-items: center;
                    justify-content: space-between;
                    gap: 12px;
                    max-width: 1240px;
                    margin: 0 auto;
                    padding: 40px clamp(22px, 5vw, 56px) 54px;
                    font-size: 10px;
                    letter-spacing: .14em;
                    text-transform: uppercase;
                    color: rgba(244,249,236,.5);
                    border-top: 1px dashed rgba(238,244,222,.22);
                }
                .ct-foot p { margin: 0; }
                .ct-foot a { color: rgba(244,249,236,.72); text-decoration: none; }
                .ct-foot a:hover { color: #FFFCF4; }

                :where(.ct-root) a:focus-visible,
                :where(.ct-root) button:focus-visible,
                :where(.ct-root) input:focus-visible,
                :where(.ct-root) select:focus-visible,
                :where(.ct-root) textarea:focus-visible {
                    outline: 2px dashed #B23A2B;
                    outline-offset: 4px;
                }
                :where(.ct-root) .ct-foot a:focus-visible,
                :where(.ct-root) .ct-btn:focus-visible {
                    outline: 2px dashed #F2E9CF;
                    outline-offset: 5px;
                }

                /* ── the board at other sizes ────────────────────────── */
                @media (max-width: 1000px) {
                    .ct-hero { grid-template-columns: 1fr; gap: 44px; padding-top: clamp(96px, 14vh, 150px); }
                    .ct-brief { grid-template-columns: 1fr; }
                    .ct-side { max-width: 380px; }
                }
                @media (max-width: 640px) {
                    .ct-root { --minor: 22px; --major: 110px; }
                    .ct-row { grid-template-columns: 1fr; gap: 0; }
                    .ct-heronotes { grid-template-columns: 1fr; }
                    .ct-cut--yellow { margin-left: 0; }
                    .ct-scissors { display: none; }
                    .ct-foot { justify-content: flex-start; }
                }
                @media (prefers-reduced-motion: reduce) {
                    [data-reveal], [data-hero] { opacity: 1; }
                    .ct-veil { display: none; }
                    .ct-shot img { transform: none; }
                    .ct-ring path, .ct-chipring path { stroke-dashoffset: 0; animation: none; }
                    .ct-done, .ct-stamp { animation: none; }
                    .ct-card, .ct-noteslip, .ct-chip, .ct-btn { transition: none; }
                }
            `}</style>
        </div>
    )
}
